"use client"

import { Link } from "@tanstack/react-router"
import { ArrowDown, ArrowUp, ChevronRight, NotebookTabs } from "lucide-react"
import { useMemo, useState } from "react"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { trpc } from "@/lib/trpc"
import { formatDate, getFullName, getInitials } from "@/utils/helpers"

export interface ClientEstateListItem {
  id: string
  firstName: string | null
  lastName: string | null
  email: string | null
  createdAt: Date | string | null
  updatedAt: Date | string | null
}

type ClientEstateWithReadiness = ClientEstateListItem & {
  readinessScore: number | null
}

type SortKey = "readiness" | "name" | "created"
type SortDirection = "asc" | "desc"

interface ClientEstatesListProps {
  clients: ClientEstateListItem[]
  isLoading?: boolean
}

function scoreColor(score: number) {
  if (score < 60) return "bg-destructive"
  if (score === 100) return "bg-success"
  return "bg-primary"
}

function clientName(client: ClientEstateWithReadiness) {
  return getFullName(client.firstName, client.lastName) || "Unnamed client"
}

function dateTimestamp(value: Date | string | null) {
  if (!value) return null

  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

const SKELETON_ROW_COUNT = 3

const SKELETON_ROW_IDS = Array.from(
  { length: SKELETON_ROW_COUNT },
  (_, index) => `client-skeleton-${index + 1}`,
)

function ClientEstateSkeletonRows() {
  return (
    <div aria-busy="true" className="divide-y">
      {SKELETON_ROW_IDS.map((rowId) => (
        <div
          key={rowId}
          className="grid gap-6 px-5 py-6 md:grid-cols-[minmax(0,1.4fr)_minmax(16rem,0.8fr)_auto] md:items-center md:px-7"
        >
          <div className="flex min-w-0 items-center gap-4">
            <Skeleton className="size-12 shrink-0 rounded-full" />

            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-5 w-40 max-w-full" />
              <Skeleton className="h-4 w-56 max-w-full" />
              <Skeleton className="h-4 w-32 max-w-full" />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between gap-4">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 w-12" />
            </div>

            <Skeleton className="h-2 w-full rounded-full" />
          </div>

          <Skeleton className="h-10 w-full md:w-32" />
        </div>
      ))}
    </div>
  )
}

export function ClientEstatesList({ clients, isLoading = false }: ClientEstatesListProps) {
  const [sortKey, setSortKey] = useState<SortKey>("readiness")
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc")

  const readinessQueries = trpc.useQueries((t) =>
    clients.map((client) =>
      t.estates.getReadinessScore(
        { estateId: client.id },
        {
          staleTime: 60_000,
          retry: 1,
        },
      ),
    ),
  )

  const clientsWithReadiness = useMemo<ClientEstateWithReadiness[]>(
    () =>
      clients.map((client, index) => ({
        ...client,
        readinessScore:
          readinessQueries[index]?.data?.score !== undefined
            ? Math.round(readinessQueries[index].data.score)
            : null,
      })),
    [clients, readinessQueries],
  )

  const isReadinessLoading = readinessQueries.some((query) => query.isPending)

  const listIsLoading = isLoading || isReadinessLoading

  const scoredClients = clientsWithReadiness.filter((client) => client.readinessScore !== null)

  const averageReadiness = scoredClients.length
    ? Math.round(
        scoredClients.reduce((sum, client) => sum + (client.readinessScore ?? 0), 0) /
          scoredClients.length,
      )
    : null

  const sortedClients = useMemo(() => {
    const direction = sortDirection === "asc" ? 1 : -1

    return [...clientsWithReadiness].sort((left, right) => {
      let comparison = 0
      if (sortKey === "name") {
        comparison = clientName(left).localeCompare(clientName(right), undefined, {
          sensitivity: "base",
        })
      } else if (sortKey === "created") {
        const leftTimestamp = dateTimestamp(left.createdAt)
        const rightTimestamp = dateTimestamp(right.createdAt)

        // Keep clients without valid dates at the end.
        if (leftTimestamp === null && rightTimestamp === null) return 0
        if (leftTimestamp === null) return 1
        if (rightTimestamp === null) return -1

        comparison = leftTimestamp - rightTimestamp
      } else {
        // Keep clients without a score at the end in either direction.
        if (left.readinessScore === null) return 1
        if (right.readinessScore === null) return -1
        comparison = left.readinessScore - right.readinessScore
      }
      return comparison * direction
    })
  }, [clientsWithReadiness, sortDirection, sortKey])

  function changeSort(nextKey: SortKey) {
    if (nextKey === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"))
      return
    }
    setSortKey(nextKey)
    setSortDirection(nextKey === "name" ? "asc" : "desc")
  }

  return (
    <section className="overflow-hidden rounded-2xl border bg-background shadow-sm">
      <header className="flex flex-col gap-5 border-b px-5 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-7">
        <div className="flex items-center gap-4">
          <span className="grid size-12 place-items-center rounded-xl bg-slate-100 text-slate-700">
            <NotebookTabs aria-hidden="true" className="size-6" />
          </span>
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Client Estates</h2>
            {listIsLoading ? (
              <Skeleton className="mt-1 h-4 w-16" />
            ) : (
              <p className="text-sm text-muted-foreground">
                {clients.length} {clients.length === 1 ? "client" : "clients"}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-baseline gap-2 sm:text-right">
          {listIsLoading ? (
            <>
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-5 w-32" />
            </>
          ) : (
            <>
              <strong className="text-4xl font-semibold tracking-tight tabular-nums">
                {averageReadiness === null ? "—" : `${averageReadiness}%`}
              </strong>
              <span className="text-muted-foreground">average readiness</span>
            </>
          )}
        </div>
      </header>

      <div className="flex items-center justify-start gap-3 border-b bg-muted/20 px-5 py-4 sm:justify-end sm:px-7">
        <span className="text-sm text-muted-foreground">Sort</span>
        <div className="inline-flex rounded-xl bg-muted p-1">
          {(["readiness", "name", "created"] as const).map((key) => {
            const selected = sortKey === key
            const SortIcon = sortDirection === "asc" ? ArrowUp : ArrowDown
            return (
              <button
                key={key}
                type="button"
                disabled={listIsLoading}
                aria-pressed={selected}
                onClick={() => changeSort(key)}
                className={`inline-flex min-h-9 items-center gap-1 rounded-lg px-3 text-sm font-medium capitalize transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 ${
                  selected
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {key}
                {selected && <SortIcon aria-hidden="true" className="size-3.5" />}
              </button>
            )
          })}
        </div>
      </div>

      {listIsLoading && <ClientEstateSkeletonRows />}

      {!listIsLoading && sortedClients.length === 0 && (
        <div className="px-6 py-16 text-center text-muted-foreground">No client estates yet.</div>
      )}

      {!listIsLoading && sortedClients.length > 0 && (
        <ul className="divide-y">
          {sortedClients.map((client) => {
            const score = client.readinessScore
            const updatedDate = formatDate(client.updatedAt)

            return (
              <li
                key={client.id}
                className="grid gap-6 px-5 py-6 md:grid-cols-[minmax(0,1.4fr)_minmax(16rem,0.8fr)_auto] md:items-center md:px-7"
              >
                <div className="flex min-w-0 items-center gap-4">
                  <span
                    aria-hidden="true"
                    className="grid size-12 shrink-0 place-items-center rounded-full text-sm font-semibold text-primary bg-primary/15"
                  >
                    {getInitials(client.firstName, client.lastName)}
                  </span>

                  <div className="min-w-0">
                    <p className="truncate font-semibold">{clientName(client)}</p>

                    <p className="truncate text-sm text-muted-foreground">
                      {client.email || "No email"}
                    </p>

                    {updatedDate && (
                      <p className="text-sm text-muted-foreground">Updated {updatedDate}</p>
                    )}
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-baseline justify-between gap-4">
                    <span className="text-sm text-muted-foreground">Readiness</span>
                    <span
                      className={`font-semibold tabular-nums ${
                        score === 100 ? "text-success" : ""
                      }`}
                    >
                      {score === null ? "Not available" : `${score}%`}
                    </span>
                  </div>

                  <div
                    role="progressbar"
                    aria-label={`${clientName(client)} estate readiness`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={score ?? undefined}
                    className="h-2 overflow-hidden rounded-full bg-muted"
                  >
                    {score !== null && (
                      <div
                        className={`h-full rounded-full ${scoreColor(score)}`}
                        style={{
                          width: `${Math.min(100, Math.max(0, score))}%`,
                        }}
                      />
                    )}
                  </div>
                </div>

                <Button asChild variant="outline" className="w-full gap-2 md:w-auto">
                  <Link to="/professional/overview/$estateId" params={{ estateId: client.id }}>
                    Dashboard
                    <ChevronRight aria-hidden="true" className="size-4" />
                  </Link>
                </Button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
